import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Autocomplete,
  TextField,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { getAllWallets, getWalletById } from "services/walletManagement";
import { exportToExcel, exportToCSV } from "utils/exportUtils";
import { getAllWalletTransactions } from "services/walletTransaction";
import { getDrivers, getDriverDetails } from "services/driverService";

const WalletsPage = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [driverFilter, setDriverFilter] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const driverSearchRef = useRef(null);

  // pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // sentinel option for "All Drivers"
  const ALL_DRIVERS_OPTION = { id: "__ALL__", fullName: "All Drivers", phone: "" };

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverInputValue, setDriverInputValue] = useState("");

  // Returns the primary driver id to use for actions: prefer the currently
  // opened wallet's driver (when dialog open) otherwise the selected driver
  // from the filters toolbar.
  const getPrimaryDriverId = () => {
    if (selected) return selected.driverId || selected.driver?.id || null;
    if (selectedDriver) return selectedDriver.id;
    return null;
  };

  const navigate = useNavigate();

  const fetchWallets = async (opts = {}) => {
    setLoading(true);
    try {
      const merged = {
        // allow callers to override pageNumber/pageSize, otherwise use current state
        pageNumber: typeof opts.pageNumber !== 'undefined' ? opts.pageNumber : page,
        pageSize: typeof opts.pageSize !== 'undefined' ? opts.pageSize : rowsPerPage,
        driverId: typeof opts.driverId !== 'undefined' ? opts.driverId : (selectedDriver ? selectedDriver.id : null),
        ...opts,
      };

      const data = await getAllWallets(merged);

      // handle pagination count
      if (data && (typeof data.totalCount !== 'undefined' || typeof data.count !== 'undefined')) {
        setTotalCount(typeof data.totalCount !== 'undefined' ? data.totalCount : data.count);
      } else if (Array.isArray(data)) {
        setTotalCount(data.length);
      } else if (data && Array.isArray(data.items)) {
        setTotalCount(data.totalCount || data.items.length);
      }

      // extract items array (compat)
      if (!data) {
        setWallets([]);
      } else if (Array.isArray(data)) {
        setWallets(data);
      } else if (Array.isArray(data.items)) {
        setWallets(data.items);
      } else if (Array.isArray(data.list)) {
        setWallets(data.list);
      } else {
        const arr = Object.values(data).find((v) => Array.isArray(v));
        setWallets(arr || []);
      }
    } catch (err) {
      console.error("Failed to load wallets", err);
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallets();
    // fetch drivers for autocomplete
    const fetchDrivers = async () => {
      setDriversLoading(true);
      try {
        // Fetch both driver types and merge results to ensure expedited drivers are present
        const [r1, r2] = await Promise.all([
          getDrivers(1000, 1, "", "", "", [], null, null, null, null, null, 1),
          getDrivers(1000, 1, "", "", "", [], null, null, null, null, null, 2),
        ]);
        const a = r1?.page || [];
        const b = r2?.page || [];
        const combined = [...a];
        const ids = new Set(a.map((d) => String(d.id)));
        for (const d of b) {
          if (!ids.has(String(d.id))) {
            combined.push(d);
            ids.add(String(d.id));
          }
        }
        setDrivers(combined);
      } catch (err) {
        console.error("Failed to fetch drivers", err);
        setDrivers([]);
      } finally {
        setDriversLoading(false);
      }
    };

    fetchDrivers();

    // cleanup on unmount
    return () => {
      if (driverSearchRef.current) clearTimeout(driverSearchRef.current);
    };
  }, []);

  // debounced search helper (component scope so Autocomplete can call it)
  const searchDrivers = (term) => {
    if (driverSearchRef.current) clearTimeout(driverSearchRef.current);
    driverSearchRef.current = setTimeout(async () => {
      setDriversLoading(true);
      try {
        const [r1, r2] = await Promise.all([
          getDrivers(100, 1, term, "", "", [], null, null, null, null, null, 1),
          getDrivers(100, 1, term, "", "", [], null, null, null, null, null, 2),
        ]);
        const a = r1?.page || [];
        const b = r2?.page || [];
        const combined = [...a];
        const ids = new Set(a.map((d) => String(d.id)));
        for (const d of b) {
          if (!ids.has(String(d.id))) {
            combined.push(d);
            ids.add(String(d.id));
          }
        }
        // Preserve the currently selected driver if it's missing from results
        // so the Autocomplete keeps showing the selection.
        if (selectedDriver && !combined.some((d) => String(d.id) === String(selectedDriver.id))) {
          setDrivers([selectedDriver, ...combined]);
        } else {
          setDrivers(combined || []);
        }
      } catch (e) {
        console.error('Driver search failed', e);
      } finally {
        setDriversLoading(false);
      }
    }, 300);
  };

  // Safe date formatter used for recent activity
  const formatDateSafe = (v) => {
    if (!v) return '-';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '-';
    if (d.getFullYear && d.getFullYear() < 1900) return '-';
    return d.toLocaleString();
  };

  const openDetails = async (id) => {
    setDialogOpen(true);
    setDetailLoading(true);
    try {
      const data = await getWalletById(id);
      let wallet = data || null;

      // If API returned no nested driver object but we have a driverId
      // try to fetch driver details to show name/phone in the dialog.
      if (wallet && !wallet.driver && (wallet.driverId || wallet.driverId === 0)) {
        try {
          const drv = await getDriverDetails(wallet.driverId);
          // Some driver endpoints return the object under data or directly
          wallet = { ...wallet, driver: drv || drv?.data || null };
        } catch (e) {
          console.warn('Failed to fetch driver details for wallet dialog', e);
        }
      }

      setSelected(wallet);
      // Fetch latest transactions for this wallet to show in Recent Activity
      try {
        const filter = {
          pageNumber: 1,
          // increase pageSize to improve chance of finding a matching wallet transaction
          pageSize: 50,
          walletId: id,
          sortBy: "timestamp",
          sortDescending: true,
        };
        const txData = await getAllWalletTransactions(filter).catch(() => null);
        let items = [];
        if (txData) {
          if (Array.isArray(txData)) items = txData;
          else if (Array.isArray(txData.items)) items = txData.items;
          else if (Array.isArray(txData.list)) items = txData.list;
          else if (Array.isArray(txData.data)) items = txData.data;
          else {
            const arr = Object.values(txData).find((v) => Array.isArray(v));
            items = arr || [];
          }
        }

        // Filter transactions client-side to ensure we pick the latest one
        // that actually belongs to this wallet. Backend may ignore walletId
        // in some cases, so we defensively match against common shapes.
        const allItems = items || [];
        const matches = allItems.filter((tx) => {
          try {
            if (tx == null) return false;
            if (typeof tx.walletId !== 'undefined' && tx.walletId !== null) return String(tx.walletId) === String(id);
            if (tx.wallet && (typeof tx.wallet.id !== 'undefined' && tx.wallet.id !== null)) return String(tx.wallet.id) === String(id);
            // Some backends may embed wallet under different keys; try 'accountId' as fallback
            if (typeof tx.accountId !== 'undefined' && tx.accountId !== null) return String(tx.accountId) === String(id);
            return false;
          } catch (e) {
            return false;
          }
        });

        const parseTime = (t) => {
          if (!t) return 0;
          return Date.parse(t.timestamp || t.createdAt || t.creationDate || t.date || t.time) || 0;
        };

        const sortedMatches = matches.slice().sort((a, b) => parseTime(b) - parseTime(a));
        const latest = sortedMatches[0];

        // If no matching transactions were found, log a small diagnostic to help
        // debug response shapes in development. This will not throw in prod.
        if ((!matches || matches.length === 0) && allItems.length > 0) {
          try {
            // eslint-disable-next-line no-console
            console.debug("Wallet recentActivity: no matches found for walletId", id, "returned items sample:", allItems.slice(0,3));
          } catch (e) {}
        }

        const recent = latest
          ? [{ transactionNumber: latest.transactionNumber, amount: latest.amount, direction: latest.direction ?? latest.Direction, date: latest.timestamp || latest.createdAt || latest.creationDate || latest.date || null }]
          : [];

        // merge into selected wallet object so UI picks it up
        setSelected((prev) => ({ ...(prev || {}), recentActivity: recent }));
      } catch (e) {
        // ignore recent activity errors
        console.warn('Failed to fetch recent wallet transactions', e);
      }
    } catch (err) {
      console.error("Failed to load wallet details", err);
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const [copiedId, setCopiedId] = useState(false);
  // Export menu state (for CSV/Excel)
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const handleExportMenuOpen = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportMenuClose = () => setExportAnchorEl(null);

  const getExportData = () =>
    (wallets || []).map((w) => ({
      "Driver": w.driver?.fullName || w.driverName || "-",
      "Phone": w.driver?.phone || w.driverPhoneNumber || "-",
      "Balance": (w.balance ?? 0).toFixed(2),
      "Created": w.creationDate ? new Date(w.creationDate).toLocaleString() : "-",
      "Wallet ID": w.id || "-",
    }));

  const handleExportCSV = () => { exportToCSV(getExportData(), 'wallets.csv'); handleExportMenuClose(); };
  const handleExportExcel = () => { exportToExcel(getExportData(), 'wallets.xlsx'); handleExportMenuClose(); };
  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 1800);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  // Render the dialog content depending on loading / selected state.
  const renderDialogContent = () => {
    if (detailLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!selected) {
      return <Box sx={{ p: 2 }}>No details available</Box>;
    }

    // Selected wallet view
    return (
      <Box>
        {/* Header */}
        <Box sx={{ background: 'linear-gradient(90deg, #1e2640, #2a3467)', color: '#fff', p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.12)', width: 64, height: 64 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 30 }} />
          </Avatar>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{(selected.balance ?? 0).toFixed(2)} <Box component="span" sx={{ fontSize: '0.9rem', ml: 1 }}>JOD</Box></Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Current Balance</Typography>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>Wallet ID</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: 'monospace', color: '#fff' }} variant="body2">{selected.id?.slice(0, 8) || '-'}…</Typography>
              <Tooltip title={copiedId ? 'Copied' : 'Copy full ID'}>
                <IconButton size="small" onClick={() => copyToClipboard(selected.id)} sx={{ color: '#fff' }}>
                  {copiedId ? <CheckIcon color="success" /> : <ContentCopyIcon sx={{ color: '#fff' }} />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Chip label={selected.driver?.fullName || selected.driverName || 'No Driver'} variant="outlined" />
            <Chip label={selected.driver?.phone || selected.driverPhoneNumber || '-'} variant="outlined" />
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Created</Typography>
              <Typography variant="body2">{selected.creationDate ? new Date(selected.creationDate).toLocaleString() : '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Modified</Typography>
              <Typography variant="body2">{selected.modificationDate ? new Date(selected.modificationDate).toLocaleString() : '-'}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* Recent items */}
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent Activity</Typography>
            <List dense disablePadding>
              {(selected.recentActivity || []).length === 0 ? (
                <ListItem>
                  <ListItemText primary="No recent activity" primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                </ListItem>
              ) : (
                (selected.recentActivity || []).slice(0,3).map((r, idx) => (
                  <ListItem key={idx} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#f3f4f6', color: '#333', width: 32, height: 32 }}>$</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={r.transactionNumber || '—'}
                      secondary={
                        <>
                          <Box component="span" sx={{ fontWeight: 700, color: Number(r.direction) === 1 ? 'success.main' : 'error.main' }}>
                            {r.amount != null ? `${Number(r.direction) === 1 ? '+' : '-'}${Number(Math.abs(r.amount)).toFixed(2)} JOD` : ''}
                          </Box>
                          <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>{r.amount != null ? '•' : ''} {formatDateSafe(r.date)}</Box>
                        </>
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h1" sx={{ marginBottom: "1.5rem" }}>
        Wallet Management
      </Typography>

      {/* Top-toolbar layout: Filters (title + controls) */}
      <Paper sx={{ p: 1, mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto' }} elevation={1}>
        <Box sx={{ flex: '1 1 320px' }}>
          <Typography variant="h6" sx={{ marginBottom: 1 }}>Filters</Typography>
        </Box>
        <Box sx={{ minWidth: 260, maxWidth: 420, flex: '1 1 320px' }}>
          <Autocomplete
            size="small"
            fullWidth
            options={drivers}
            getOptionLabel={(option) => {
              if (!option) return "";
              return `${option.fullName || "Unknown"} ${option.phone ? `(${option.phone})` : ""}`;
            }}
            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
            inputValue={driverInputValue}
            value={selectedDriver}
            onChange={(event, newValue) => {
              try { console.debug('Driver Autocomplete onChange, newValue=', newValue); } catch (e) {}
              setSelectedDriver(newValue || null);
              if (newValue) {
                // keep the input showing the selected driver's label
                setDriverInputValue(`${newValue.fullName || "Unknown"}${newValue.phone ? ` (${newValue.phone})` : ""}`);
                // ensure selected option remains in the drivers list so Autocomplete can render it
                setDrivers((prev) => {
                  if (!prev || !Array.isArray(prev)) return [newValue];
                  if (prev.some((d) => String(d.id) === String(newValue.id))) return prev;
                  return [newValue, ...prev];
                });
              } else {
                setDriverInputValue("");
              }
            }}
            onInputChange={(event, newInputValue) => {
              setDriverInputValue(newInputValue);
              try { if (newInputValue && newInputValue.trim().length > 0) searchDrivers(newInputValue); } catch (e) {}
            }}
            // make selection more immediate on click/enter
            autoSelect={true}
            openOnFocus={true}
            loading={driversLoading}
            ListboxProps={{ style: { maxHeight: 300 } }}
            renderInput={(params) => (
              <TextField {...params} label="Driver" placeholder="Search driver by name or phone" margin="none" />
            )}
            noOptionsText="No drivers found"
          />
          {/* driver count removed per design parity */}
        </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              onClick={() => { setPage(0); fetchWallets({ driverId: selectedDriver ? selectedDriver.id : null, pageNumber: 0 }); }}
              sx={{ backgroundColor: "#2A3467", height: 36 }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={() => { setSelectedDriver(null); setDriverInputValue(""); setPage(0); fetchWallets({ driverId: null, pageNumber: 0 }); }}
              sx={{ height: 36 }}
            >
              Reset
            </Button>

            {/* Action buttons moved into the same toolbar, right-aligned */}
            <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                sx={{ backgroundColor: '#2A3467' }}
                onClick={() => {
                  const driverId = getPrimaryDriverId();
                  navigate('/wallet-transactions', { state: { openCreate: true, transactionType: 1, driverId } });
                  setDialogOpen(false);
                }}
              >
                Top Up
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  const driverId = getPrimaryDriverId();
                  navigate('/wallet-transactions', { state: { openCreate: true, transactionType: 5, driverId } });
                  setDialogOpen(false);
                }}
              >
                Withdraw
              </Button>
              <IconButton aria-label="Export" onClick={handleExportMenuOpen} sx={{ border: '1px solid #2A3467', color: '#2A3467', ml: 1 }}>
                <FileDownloadIcon />
              </IconButton>
              <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={handleExportMenuClose}>
                <MenuItem onClick={handleExportCSV}>
                  <ListItemIcon><TableChartIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Export as CSV</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleExportExcel}>
                  <ListItemIcon><TableChartIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Export as Excel</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
      </Paper>

        {/* Duplicate header actions removed — actions now live in the top filters toolbar */}

      <Paper>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Driver</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wallets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: "center", p: 4 }}>
                      No wallets found
                    </TableCell>
                  </TableRow>
                ) : (
                  wallets.map((w) => (
                    <TableRow key={w.id} sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                      <TableCell>{w.driver?.fullName || w.driverName || "-"}</TableCell>
                      <TableCell>{w.driver?.phone || w.driverPhoneNumber || "-"}</TableCell>
                      <TableCell align="right">{(w.balance ?? 0).toFixed(2)} JOD</TableCell>
                      <TableCell>{w.creationDate ? new Date(w.creationDate).toLocaleDateString() : "-"}</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => openDetails(w.id)}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </TableContainer>

            <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => {
              setPage(newPage);
              fetchWallets({ pageNumber: newPage, pageSize: rowsPerPage });
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              const newSize = parseInt(event.target.value, 10);
              setRowsPerPage(newSize);
              setPage(0);
              fetchWallets({ pageNumber: 0, pageSize: newSize });
            }}
            rowsPerPageOptions={[5,10,25,50]}
            />
          </>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          {renderDialogContent()}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WalletsPage;
